import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { lighten, desaturate, darken } from 'polished';
import ReactDOM from 'react-dom';
import { Modifier, usePopper } from 'react-popper';
import { SAIcon, SAIcons, SAIconSize, SAPopover, SAUXTheme } from '@saux/design-system-react';
// import { SAInputContainer } from '../input/input.component';
// import { SAUX360Theme, SAUXTheme } from '../theme';
// import { SAIcons } from '../icon';
// import filterIcon from '../../../icons/Union.png';
// import Filter from '../filter/Filter';


interface SuggestionsProps {
  theme: SAUXTheme;
  color?: string;
}

const Wrapper = styled.div<SuggestionsProps>`
  ${({ theme, color }) => {
    const baseColor = color || theme.color.primary.background;
    return css`
      position: relative;
      &:hover {
        .icon-button {
          display: flex;
          align-items:center;
        }
      }
      .icon-button {
        display: none;
        line {
          stroke: ${baseColor};
        }
      }
    `;
  }}
`;


const TextLikeInput = styled.div<SuggestionsProps>`
${({ theme, color }) => {
    return css`
        width:100%;
        height:50px;
        align-items: center;
        display: inline-flex;
        &.fullWidthInput {
          display: flex;
        }
        position: relative;
        padding-top: 5px;
        
        svg{
          left:5px;
          position:absolute;
        }
        .icons{
          display:flex;
          align-items:center;
          position:absolute;
          right:50px;
          svg:hover{
            cursor:pointer
          }
          img{
            margin-left:50px;
            cursor:pointer;
          }
        }
  `;
  }}


`;
const Input = styled.input<SuggestionsProps>`
${({ theme, color }) => {
    const baseColor = color || theme?.color.primary.background;
    return css`
        width: 100%;
        height:100%;
        border-radius:5px;
        border: 1px solid #dedede;
        padding-left:40px;
        padding-right:40px;
        :active,
        :focus {
          outline: none !important;
          border-color:${baseColor};
        }
        ::-webkit-input-placeholder { /* Edge */
          color: #979797;
          font-size: 16px;
          font-family: PT Sans;
          font-style: normal;
          font-weight: normal;
        }
        
        :-ms-input-placeholder { /* Internet Explorer */
          color: #979797;
          font-size: 16px;
          font-family: PT Sans;
          font-style: normal;
          font-weight: normal;
        }
        
        ::placeholder {
          color: #979797;
          font-size: 16px;
          font-family: PT Sans;
          font-style: normal;
          font-weight: normal;
        }
  `;
  }}
`;


const Box = styled.ul<SuggestionsProps>`
  ${({ theme, color }) => {
    const baseColor = color || theme.color.primary.background;
    return css`
      background: #fff;
      list-style: none;
      padding:0;
      height: auto;
      border-radius:5px;
      max-height: 55vh;
      border: 1px solid ${baseColor};
      overflow: auto;
      overflow: overlay;
      scroll-behavior: smooth;
      transition: transform 0.15s;
      box-shadow: ${theme.shadow.elevation.level1};
      z-index: 1;

      .claim-record:nth-child(even) {
        background: #F8F9FC
      };

      &::-webkit-scrollbar {
        width: 20px;
        background-color: rgba(255, 255, 255, 0);
      }

      ::-webkit-scrollbar-track,
      ::-webkit-scrollbar-thumb {
        border: 5px solid rgba(255, 255, 255, 0);
        background-clip: padding-box;
        
      }

      &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 0 2px 2px 0;
        &:hover {
          background: ${desaturate(0.7, lighten(0.3, baseColor))};
        }
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0);
        // border-radius: 500px;
        margin: 0 2px;
      }
      [data-popper-placement^='top'] & {
        margin-bottom: 10px;
      }
      [data-popper-placement^='bottom'] & {
        margin-top: 0px;
      }
      li {
        padding: ${theme.size.spacer.small};
        padding-left:30px;
        padding-right:30px;
        display: flex;
        width:100%;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        // color: ${darken(0.15, baseColor)};
        // &.selected {
        //   background: ${desaturate(0.5, lighten(0.4, baseColor))};
        //   padding-left:20px;
        // }
        &:hover {
          background: ${desaturate(0.5, lighten(0.5, baseColor))};
          cursor: pointer;
          border-left: 10px solid rgba(147, 37, 178, 1);
          padding-left:20px;
        }
        .highlighted {
          font-weight: bold;
          color:#0173C6
        }
      }
    `;
  }}
`;

export default function SearchSuggestion({
  placeholder,
  hint,
  options,
  id,
  color,
  className,
  onSelect,
  onClear,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error,
  theme,
  value,
  fullWidth,
  ...rest
}: any) {
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [userInput, setUserInput] = useState(value);
  const [dropdownVisible, setDropdownVisible] = useState(true);

  // refs

  const selectedRef = React.useRef<HTMLLIElement | null>(null);

  // Popper

  const [referenceElement, setReferenceElement] = useState<HTMLInputElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [popoverReferenceElement, setPopoverReferenceElement] = useState<Element | null>(null);

  const modifiers = useMemo(
    () => [
      {
        name: 'sameWidth',
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
          // state.styles.popper.transform = `translate(${state.rects.reference.x}px,230px)`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      } as Modifier<'sameWidth', { customOption: any }>,
    ],
    [],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers,
  });

  const handleUserInput = (inputValue: string) => {
    setUserInput(inputValue);
    // setSuggestions(filterSuggestions(inputValue));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUserInput(e.currentTarget.value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelection = (index: number) => {
    if (onSelect) {
      onSelect(index)
    }
    // setReactInputValue(referenceElement!, Array.isArray(selection) ? selection[0] : selection);
    setDropdownVisible(false);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setDropdownVisible(true);
    if (onFocus !== undefined) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur !== undefined) {
      onBlur(e);
    }
    setDropdownVisible(false);
  };

  const clearInput = () => {
    setUserInput('')
    if (onClear) {
      onClear()
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setDropdownVisible(true);
    if (onKeyDown !== undefined) {
      onKeyDown(e);
    }
    if (['Up', 'ArrowUp'].includes(e.key)) {
      if (activeSuggestion === 0) {
        setActiveSuggestion(options.length - 1);
        return;
      }

      setActiveSuggestion(activeSuggestion - 1);
    }
    // User pressed the down arrow
    else if (['Down', 'ArrowDown'].includes(e.key)) {
      if (activeSuggestion === options.length - 1) {
        setActiveSuggestion(0);
        return;
      }
      setActiveSuggestion(activeSuggestion + 1);
    } else if (e.key === 'Enter') {
      handleSelection(activeSuggestion);
    } else {
      setActiveSuggestion(0);
    }
  };

  useEffect(() => {
    if (selectedRef.current && popperElement) {
      popperElement.children[0].scrollTop = selectedRef.current.offsetTop;
    }
  }, [activeSuggestion]);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    // setPopoverReferenceElement(event.currentTarget);
  }

  function handleClose(event: React.MouseEvent<HTMLElement> | MouseEvent, index: number | null) {
    setPopoverReferenceElement(null);
  }

  return (
    <Wrapper data-testid="testid" color={color} theme={theme}>
      <>
        <div className={popoverReferenceElement ? "popup" : ""}
          style={popoverReferenceElement ? {
            background: "rgba(0, 0, 0, 0.8)",
            top: 0,
            left: 0,
            margin: 0,
            padding: 0,
            width: "100%",
            height: "100%",
            zIndex: 1400
          } : {}}
        >
          <TextLikeInput theme={theme}  >
            <SAIcon icon={SAIcons.search} size={SAIconSize.medium} colorVariant={"#979797"} />
            <Input
              type="text"
              onChange={handleChange}
              // onKeyDown={handleKeyDown}
              onFocus={(e) => handleFocus(e)}
              onBlur={(e) => handleBlur(e)}
              placeholder={placeholder}
              value={userInput}
              aria-activedescendant={dropdownVisible ? `${id}-option-${activeSuggestion}` : undefined}
              aria-autocomplete="list"
              role="combobox"
              aria-controls={`${id}-list`}
              aria-expanded={dropdownVisible && options.length > 0}
              ref={setReferenceElement}
              autoComplete="off"
              {...rest}
            />
            <div className="icons">
              <div className="icon-button" onClick={clearInput}>
                <SAIcon icon={SAIcons.x} size={SAIconSize.medium} colorVariant={"secondary"} />
              </div>
              {/* <div onClick={handleClick}>
                <img src={filterIcon} width={30} height={30}></img>
              </div> */}
            </div>
          </TextLikeInput>
        </div>
        {/* <Filter /> */}
        {dropdownVisible &&
          options.length > 0 &&
          ReactDOM.createPortal(
            <div
              ref={setPopperElement}
              style={styles.popper}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...attributes.popper}
            >
              <Box role="listbox" color={color} id={`${id}-list`} theme={theme}>
                {options.map((item: any, index: number) => {
                  const selected = index === activeSuggestion;
                  return (
                    <div className={`claim-record ${selected ? " active" : " "}`} style={{ display: "flex", width: "100%" }}>
                      {/* {selected ? <div style={{ width: 10, backgroundColor: "rgba(147, 37, 178, 1)" }}></div> : <></>} */}
                      <li
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelection(index)}
                        // onKeyDown={(e) => e.key === 'Enter' && handleSelection(index)}
                        aria-selected={selected}
                        role="option"
                        key={item}
                        className={selected ? 'selected' : ''}
                        id={`${id}-option-${index}`}
                        ref={selected ? selectedRef : undefined}
                        tabIndex={-1}
                      >
                        {Array.isArray(item) ? item[0] : item}
                        {/* {Array.isArray(item) && <span className="line2">{item[1]}</span>} */}
                      </li>
                    </div>
                  );
                })}
              </Box>
            </div>,
            document.body,
          )}
        {<SAPopover
          onClose={(e: MouseEvent, index: number | null) => handleClose(e, index)}
          placement="bottom-start"
          referenceElement={popoverReferenceElement}
          variant="popover-menu"
          background="rgba(0, 0, 0, 0.5)"
        >
          <div>hello</div>
        </SAPopover>}
      </>
    </Wrapper>
  );
}

